/* eslint-disable @next/next/no-img-element */
import React, { useContext, useState } from "react";

import classNames from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/legacy/image";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import Markdown from "../../styles/Markdown";

/**
 * HoverRevealCard Component
 *
 * Expected props from ComponentContext:
 * - animatedItems: Array of items with structure:
 *   {
 *     uniqueId: string,
 *     title: string,                    // Card title (optional)
 *     itemBeneathImage: string,         // Markdown content shown beneath image in normal state
 *     itemAppearingOnHover: string,    // Markdown content that appears on hover
 *     url: string,                     // Optional URL to make card clickable
 *     image: {
 *       data: {
 *         id: string,
 *         attributes: {
 *           url: string,
 *           alternativeText: string,
 *           width: number,
 *           height: number
 *         }
 *       }
 *     }
 *   }
 * - imagesInRow: "one" | "two" | "three" | "four" | "five" | "six"
 * - objectFit: "cover" | "contain" | "fill" | "scale-down" | "none" (default: "cover")
 * - imageAspectRatio: number (default: 75) - percentage for normal state image height
 * - hoverImageAspectRatio: number (default: 50) - percentage for hover state image height
 * - zoomScale: number (default: 1.15) - how much the image zooms on hover
 * - animationDuration: number (default: 0.4) - animation duration in seconds
 * - cardSpacing: string (default: "30px") - gap between cards
 * - borderRadius: string (default: "12px") - card border radius
 * - enableCardLift: boolean (default: true) - whether card lifts on hover
 * - mobileHeight: string (default: "400px") - card height on mobile
 * - desktopHeight: string (default: "400px") - card height on desktop
 * - CSS: string (custom styles)
 * - uniqueId: string
 * - wrapperClassName: string
 *
 * Behavior:
 * - Normal state: Image (imageAspectRatio% of card) with itemBeneathImage below
 * - Hover state: Image shrinks (hoverImageAspectRatio% of card) and zooms, itemBeneathImage moves up,
 *   itemAppearingOnHover slides up from bottom
 */

const StyledWrapper = styled.div`
  ${(props) => props.dynamicStyles}
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ cardSpacing }) => cardSpacing || "20px"};
`;

const StyledCard = styled(motion.div)`
  position: relative;
  flex: 0 0 calc(100% - 30px);
  max-width: calc(100% - 30px);
  height: ${({ mobileHeight }) => mobileHeight || "400px"};
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: ${({ borderRadius }) => borderRadius || "8px"};

  @media (min-width: 481px) {
    flex: 0 0 calc(50% - 30px);
    max-width: calc(50% - 30px);
  }

  @media (min-width: 769px) {
    flex: 0 0 ${({ columnWidth }) => `calc(${columnWidth}% - 20px)`};
    max-width: ${({ columnWidth }) => `calc(${columnWidth}% - 20px)`};
    height: ${({ desktopHeight }) => desktopHeight || "400px"};
  }
`;

const StyledClickableCard = styled(motion.a)`
  position: relative;
  flex: 0 0 calc(100% - 30px);
  max-width: calc(100% - 30px);
  height: ${({ mobileHeight }) => mobileHeight || "400px"};
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: ${({ borderRadius }) => borderRadius || "8px"};
  text-decoration: none;
  color: inherit;

  @media (min-width: 481px) {
    flex: 0 0 calc(100% - 30px);
    max-width: calc(100% - 30px);
  }

  @media (min-width: 769px) {
    flex: 0 0 ${({ columnWidth }) => `calc(${columnWidth}% - 30px)`};
    max-width: ${({ columnWidth }) => `calc(${columnWidth}% - 30px)`};
    height: ${({ desktopHeight }) => desktopHeight || "400px"};
  }
`;

const StyledImageContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: ${({ imageAspectRatio }) => imageAspectRatio || 90}%;
  overflow: hidden;
`;

const StyledImage = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledContent = styled(motion.div)`
  position: relative;
  padding: 20px;
  z-index: 2;
`;

const StyledHoverContent = styled(motion.div)`
  position: relative;
  padding: 20px;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  z-index: 3;
`;

function HoverRevealCard() {
  const {
    imagesInRow,
    animatedItems,
    CSS,
    uniqueId,
    wrapperClassName,
    objectFit = "cover",
    imageAspectRatio = 75,
    hoverImageAspectRatio = 50,
    zoomScale = 1.15,
    animationDuration = 0.4,
    cardSpacing = "30px",
    borderRadius = "12px",
    enableCardLift = true,
    mobileHeight = "400px",
    desktopHeight = "400px",
  } = useContext(ComponentContext);
  const [hoveredCard, setHoveredCard] = useState(null);

  const imagesRowMap = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
  };

  const columnWidth = 100 / (imagesRowMap[imagesInRow] || 1);

  const handleMouseEnter = (cardId) => {
    setHoveredCard(cardId);
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
  };

  const cardVariants = {
    rest: {
      y: 0,
      scale: 1,
    },
    hover: enableCardLift
      ? {
          y: -5,
          scale: 1.02,
        }
      : {
          y: 0,
          scale: 1,
        },
  };

  const imageContainerVariants = {
    rest: {
      paddingBottom: `${imageAspectRatio}%`,
    },
    hover: {
      paddingBottom: `${hoverImageAspectRatio}%`,
    },
  };

  const imageVariants = {
    rest: {
      scale: 1,
    },
    hover: {
      scale: zoomScale,
    },
  };

  const contentVariants = {
    rest: {
      opacity: 0.9,
      y: 10,
    },
    hover: {
      opacity: 1,
      y: 0,
    },
  };

  const hoverContentVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  return (
    <StyledWrapper
      className={classNames(wrapperClassName)}
      id={uniqueId}
      dynamicStyles={CSS}
      cardSpacing={cardSpacing}
    >
      {animatedItems?.map((item) => {
        const isHovered = hoveredCard === item.uniqueId;

        const cardContent = (
          <>
            <StyledImageContainer
              imageAspectRatio={imageAspectRatio}
              variants={imageContainerVariants}
              initial="rest"
              animate={isHovered ? "hover" : "rest"}
              transition={{ duration: animationDuration }}
            >
              {item?.image?.id ? (
                <StyledImage
                  variants={imageVariants}
                  initial="rest"
                  animate={isHovered ? "hover" : "rest"}
                  transition={{ duration: animationDuration }}
                >
                  <Image
                    alt={item.image?.alternativeText || "Image"}
                    src={item.image?.url}
                    layout="fill"
                    objectFit={objectFit}
                  />
                </StyledImage>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  No Image
                </div>
              )}
            </StyledImageContainer>

            {/* Normal state content - itemBeneathImage */}
            <StyledContent
              variants={contentVariants}
              initial="rest"
              animate={isHovered ? "hover" : "rest"}
              transition={{ duration: animationDuration }}
            >
              {item.itemBeneathImage && (
                <Markdown>{item.itemBeneathImage}</Markdown>
              )}
            </StyledContent>

            {/* Hover state content - itemAppearingOnHover */}
            <AnimatePresence>
              {isHovered && (
                <StyledHoverContent
                  variants={hoverContentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  transition={{ duration: animationDuration }}
                >
                  {item.itemAppearingOnHover && (
                    <Markdown>{item.itemAppearingOnHover}</Markdown>
                  )}
                </StyledHoverContent>
              )}
            </AnimatePresence>
          </>
        );

        return item.url ? (
          <StyledClickableCard
            key={item.uniqueId}
            href={item.url}
            columnWidth={columnWidth}
            borderRadius={borderRadius}
            mobileHeight={mobileHeight}
            desktopHeight={desktopHeight}
            onMouseEnter={() => handleMouseEnter(item.uniqueId)}
            onMouseLeave={handleMouseLeave}
            variants={cardVariants}
            initial="rest"
            animate={isHovered ? "hover" : "rest"}
            transition={{ duration: animationDuration }}
          >
            {cardContent}
          </StyledClickableCard>
        ) : (
          <StyledCard
            key={item.uniqueId}
            columnWidth={columnWidth}
            borderRadius={borderRadius}
            mobileHeight={mobileHeight}
            desktopHeight={desktopHeight}
            onMouseEnter={() => handleMouseEnter(item.uniqueId)}
            onMouseLeave={handleMouseLeave}
            variants={cardVariants}
            initial="rest"
            animate={isHovered ? "hover" : "rest"}
            transition={{ duration: animationDuration }}
          >
            {cardContent}
          </StyledCard>
        );
      })}
    </StyledWrapper>
  );
}

export default HoverRevealCard;
